import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE, formatApiError } from "./AuthContext";
import { ShieldAlert, X, Send, Loader2 } from "lucide-react";

// info = 409 detail {client_id, client_name, can_override, message}; onOverride(reason) retries the status change.
export default function JobIdBlockDialog({ info, onClose, onOverride }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(null);
  const sendCheck = async () => {
    setBusy("send");
    try {
      const { data } = await axios.post(`${API_BASE}/voi/sessions`, { client_id: info.client_id, channels: ["email"] });
      (data.delivery?.email?.ok ? toast.success : toast.warning)(data.delivery?.email?.ok ? "ID check link emailed" : "ID check created — email failed; copy the link from the client's ID check tab");
      onClose();
    } catch (e) {
      if (e.response?.status === 409) { toast.info("An ID check is already open for this client — see their ID check tab to resend or review it."); onClose(); }
      else toast.error(formatApiError(e));
    } finally { setBusy(null); }
  };
  const override = async () => {
    setBusy("override");
    try { await onOverride(reason.trim()); onClose(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="job-id-block-dialog">
      <div className="bg-[var(--paper)] rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border)]">
          <div className="flex items-center gap-2 text-[14px] font-medium text-[var(--accent)]"><ShieldAlert size={15} /> ID not verified</div>
          <button onClick={onClose} data-testid="job-id-block-close"><X size={17} /></button>
        </div>
        <div className="p-5 space-y-4 text-[13px]">
          <p data-testid="job-id-block-message">{info.message} Verify their identity first.</p>
          <button className="btn-secondary" onClick={sendCheck} disabled={!!busy} data-testid="job-id-block-send">
            {busy === "send" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send ID check
          </button>
          {info.can_override ? (
            <div className="space-y-2 pt-3 border-t border-[var(--border)]">
              <div className="overline">Manager override</div>
              <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500}
                        placeholder="Why is it OK to complete this job without an ID check? (recorded)"
                        className="w-full border border-[var(--border)] rounded-md px-3 py-2" data-testid="job-id-block-reason" />
              <button className="btn-primary w-full justify-center" onClick={override} disabled={!!busy || reason.trim().length < 5}
                      data-testid="job-id-block-override">
                {busy === "override" ? <Loader2 size={13} className="animate-spin" /> : null} Override and mark done
              </button>
            </div>
          ) : (
            <p className="text-[12px] text-[var(--ink-3)]" data-testid="job-id-block-no-override">Only an owner or manager can override this.</p>
          )}
        </div>
      </div>
    </div>
  );
}
